import {
    getAdapter,
    ApiRequestConfig,
    isNetworkError,
    isMaintenanceError,
    getApiInstance,
    ApigwAuthErrors,
    ApigwResponse,
    ApiResponse,
} from '@shared/helpers/api'
import {pick} from '@shared/helpers/object'

import {GwErrorName} from '@web/core/constants'

import {GwError} from './interfaces/error'

const isGetMethod = (config: ApiRequestConfig) => {
    const method = (config.method ?? 'get').toUpperCase()
    return method === 'GET'
}

const buildCacheKey = (config: ApiRequestConfig): string => {
    const base = `${config.baseURL ?? ''}${config.url ?? ''}`
    const params = config.params

    if (!params || typeof params !== 'object' || params instanceof URLSearchParams) {
        return base
    }

    const sorted = Object.entries(params as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v == null ? '' : String(v)}`)
        .join('&')

    return sorted ? `${base}?${sorted}` : base
}

/**
 * front-apigw 를 구분할 수 있는 error 값을 추가합니다.
 **/
function enhanceError(error: Error, data: ApiResponse): GwError {
    const gwError = error as GwError
    gwError.name = GwErrorName
    gwError.isGwError = true
    gwError.errorData = {
        response: {
            status: data.status,
            data: data.data,
        },
    }

    return gwError
}

const propPick = pick<ApiResponse, 'data' | 'status'>(['data', 'status'])

type Config = {
    baseURL: string
    /** 응답에 cache기능 사용 원할 경우 cache 저장지속 시간(ms) */
    cacheTTL?: number
}

/**
 * axios instance 를 생성합니다.
 **/
export function createAxiosInstance({baseURL, cacheTTL}: Config) {
    const api = getApiInstance({defaultConfig: {baseURL}})

    const cacheStore = new Map<string, Promise<ApiResponse>>()

    api.interceptors.request.use((requestConfig) => {
        const shouldUseCache = cacheTTL && cacheTTL > 0 && isGetMethod(requestConfig)

        // cacheTTL 설정 되어있을때에만 getMethod에 한해 caching전략 사용
        if (!shouldUseCache) {
            return requestConfig
        }

        const cacheKey = buildCacheKey(requestConfig)
        const cached = cacheStore.get(cacheKey)

        if (cached) {
            requestConfig.adapter = async () =>
                cached.then((response) => ({
                    ...response,
                    config: requestConfig,
                }))
            return requestConfig
        }

        const originalAdapter = getAdapter(requestConfig.adapter ?? ['xhr'])
        requestConfig.adapter = async (adapterConfig) => {
            const requestPromise = originalAdapter(adapterConfig)
            cacheStore.set(cacheKey, requestPromise)
            setTimeout(() => cacheStore.delete(cacheKey), cacheTTL)
            return requestPromise
        }

        return requestConfig
    })

    api.interceptors.response.use(
        (response) => {
            const {config: responseConfig} = response
            const isGWApi = !responseConfig.url?.includes('https')
            const isNotSuccessful = response.data?.code !== ApigwResponse.SUCCESS
            if (isGWApi && isNotSuccessful) {
                const error = new Error(JSON.stringify(propPick(response)))
                return Promise.reject(enhanceError(error, response))
            }
            return response
        },
        (error) => {
            if (isNetworkError(error)) {
                return Promise.reject(error)
            }

            if (isMaintenanceError(error)) {
                // action.error.message는 string으로 전달되므로 stringify 처리하여 error 객체를 온전히 전달
                return Promise.reject(JSON.stringify(error.response))
            }

            // TODO 타임아웃 에러도 전처리 필요

            const isGWApi = !error?.config?.url?.includes('https')
            const isAuthError = error?.response?.data && ApigwAuthErrors.includes(error?.response?.data?.code)
            if (isGWApi && isAuthError) {
                const authError = new Error(JSON.stringify(propPick(error?.response)))
                return Promise.reject(enhanceError(authError, error?.response))
            }

            return Promise.reject(error)
        },
    )

    return api
}
